export type ExampleSlug =
  | 'potree'
  | 'semantic-point-cloud'
  | 'gs3d'
  | 'gs3d-effects'
  | 'camera-animation'
  | 'gs3d-effect-animation'
  | 'gs3d-spatial-query'
  | 'gs3d-spatial-capsule-walk'
  | 'load-cloud'
  | 'mesh-damaged-helmet'
  | 'mesh-horse'
  | 'mesh-ktx2'
  | 'tiles-mesh'
  | 'three-lines'
  | 'cad'
  | 'cad-multi-viewport'
  | 'multi-viewport'

export type GraphicsControlStrategy = 'switchable' | 'blocked' | 'locked'

/**
 * route 使用的基础 preset。
 *
 * 只记录 preset 名称而不是插件实例，避免 gallery 静态引入全部插件链；
 * 控制卡需要插件清单时通过 `graphicsProbe.ts` 惰性解析。
 */
export type ExamplePresetKey =
  | 'pointCloudOnly'
  | 'pointCloudAndGSplat'
  | 'cadOnly'
  | 'cadFixture'
  | 'coreMesh'
  | 'none'


export interface ExampleCatalogEntry {
  slug: ExampleSlug
  title: string
  eyebrow: string
  description: string
  badge: string
  routePath: string
  previewClassName: string
  previewImage?: string
  keywords: string[]
  graphicsControlStrategy: GraphicsControlStrategy
  graphicsControlReason?: string
  /** 基础 preset；route 额外的插件用 `extraPluginLabels` 标注。 */
  presetKey: ExamplePresetKey
  /** route 在 preset 之外追加的插件类名，仅用于展示与文档。 */
  extraPluginLabels?: readonly string[]
}

export interface InternalExampleRouteEntry {
  routePath: string
  title: string
  helper: string
  preset: string
  assetKind: string
  graphicsControlStrategy: GraphicsControlStrategy
  graphicsControlReason: string
}

export const DEFAULT_EXAMPLE_SLUG: ExampleSlug = 'potree'

export const EXAMPLE_CATALOG: ExampleCatalogEntry[] = [
  {
    slug: 'potree',
    title: 'Potree Point Cloud',
    eyebrow: 'Point Cloud Toolkit',
    description: '点云浏览、测量、裁切与属性调试的综合示例。',
    badge: '核心示例',
    routePath: '/standalone/potree',
    previewClassName: 'preview-potree',
    previewImage: '/examples/previews/potree.jpg',
    keywords: ['potree', 'point cloud', 'measure', 'clip'],
    graphicsControlStrategy: 'switchable',
    presetKey: 'pointCloudOnly',
    extraPluginLabels: ['MeasurePlugin', 'MeasureOverlayPlugin', 'ClipPlugin', 'ClipOverlayPlugin'],
  },
  {
    slug: 'semantic-point-cloud',
    title: 'Semantic Point Cloud',
    eyebrow: 'Classification Rendering',
    description: '按 classification 映射语义颜色，并在点拾取结果中读取分类索引。',
    badge: '语义分类',
    routePath: '/standalone/semantic-point-cloud',
    previewClassName: 'preview-potree',
    previewImage: '/examples/previews/potree.jpg',
    keywords: ['semantic', 'classification', 'point cloud', 'pick', 'color map'],
    graphicsControlStrategy: 'switchable',
    presetKey: 'pointCloudOnly',
  },
  {
    slug: 'gs3d',
    title: '3D Gaussian Splatting',
    eyebrow: 'Gaussian Splat',
    description: '3DGS 模型加载、统计与视角控制示例。',
    badge: '高斯渲染',
    routePath: '/standalone/gs3d',
    previewClassName: 'preview-gs3d',
    previewImage: '/examples/previews/gs3d.jpg',
    keywords: ['3dgs', 'gaussian', 'splat', 'lod'],
    graphicsControlStrategy: 'switchable',
    presetKey: 'pointCloudAndGSplat',
    extraPluginLabels: ['ClipPlugin', 'ClipOverlayPlugin'],
  },
  {
    slug: 'gs3d-effects',
    title: '3DGS PlayCanvas Effects',
    eyebrow: 'Gaussian Splat Effects',
    description: '单独展示 PlayCanvas 风格 3DGS 盒体扫描与径向揭示特效。',
    badge: 'GSplat 特效',
    routePath: '/standalone/gs3d-effects',
    previewClassName: 'preview-gs3d',
    previewImage: '/examples/previews/gs3d.jpg',
    keywords: ['3dgs', 'gaussian', 'splat', 'shader effect', 'reveal', 'playcanvas'],
    graphicsControlStrategy: 'switchable',
    presetKey: 'pointCloudAndGSplat',
  },
  {
    slug: 'camera-animation',
    title: 'Camera Animation',
    eyebrow: 'Animation Service',
    description: '使用通用动画服务驱动 CameraRig，可导入点云或 3DGS 场景，编辑、导入导出并播放镜头关键帧轨道。',
    badge: '镜头动画',
    routePath: '/standalone/camera-animation',
    previewClassName: 'preview-gs3d',
    previewImage: '/examples/previews/gs3d.jpg',
    keywords: ['point cloud', '3dgs', 'camera', 'animation', 'timeline', 'keyframe'],
    graphicsControlStrategy: 'switchable',
    presetKey: 'pointCloudAndGSplat',
    extraPluginLabels: ['AnimationPlugin'],
  },
  {
    slug: 'gs3d-effect-animation',
    title: '3DGS Effect Timeline',
    eyebrow: 'Effect + Camera',
    description: '组合 3DGS PlayCanvas 风格特效编辑与镜头关键帧编辑，一键同时播放特效和相机动画。',
    badge: '特效编排',
    routePath: '/standalone/gs3d-effect-animation',
    previewClassName: 'preview-gs3d',
    previewImage: '/examples/previews/gs3d.jpg',
    keywords: ['3dgs', 'gaussian', 'splat', 'shader effect', 'camera', 'animation', 'timeline'],
    graphicsControlStrategy: 'switchable',
    presetKey: 'pointCloudAndGSplat',
    extraPluginLabels: ['AnimationPlugin'],
  },
  {
    slug: 'gs3d-spatial-query',
    title: '3DGS Spatial Query',
    eyebrow: 'Proxy Mesh Validation',
    description: '联调 3DGS 与隐藏 proxy mesh，验证测量和控制器是否统一命中同一表面。',
    badge: 'Spatial Query',
    routePath: '/standalone/gs3d-spatial-query',
    previewClassName: 'preview-gs3d-spatial-query',
    keywords: ['3dgs', 'proxy mesh', 'spatial query', 'measurement', 'camera control'],
    graphicsControlStrategy: 'switchable',
    presetKey: 'pointCloudAndGSplat',
    extraPluginLabels: ['SpatialQueryPlugin'],
  },
  {
    slug: 'gs3d-spatial-capsule-walk',
    title: '3DGS Spatial Capsule Walk',
    eyebrow: 'Collision Runtime Demo',
    description: '先加载 3DGS，再绑定 proxy collision world，演示胶囊体漫游、楼梯跨越与投掷球的空间运行时。',
    badge: 'Spatial FPS',
    routePath: '/standalone/gs3d-spatial-capsule-walk',
    previewClassName: 'preview-gs3d-spatial-capsule-walk',
    keywords: ['3dgs', 'capsule', 'fps', 'collision', 'proxy mesh', 'stairs'],
    graphicsControlStrategy: 'switchable',
    presetKey: 'pointCloudAndGSplat',
    extraPluginLabels: ['CharacterPlugin'],
  },
  {
    slug: 'load-cloud',
    title: 'Minimal Loader',
    eyebrow: 'Point Cloud Baseline',
    description: '最小点云加载场景，适合验证基础加载链路。',
    badge: '轻量验证',
    routePath: '/standalone/load-cloud',
    previewClassName: 'preview-load-cloud',
    previewImage: '/examples/previews/load-cloud.jpg',
    keywords: ['load', 'minimal', 'baseline', 'point cloud'],
    graphicsControlStrategy: 'switchable',
    presetKey: 'pointCloudOnly',
  },
  {
    slug: 'tiles-mesh',
    title: '3D Tiles Mesh',
    eyebrow: 'Streaming Mesh / M0',
    description: '两份本地 3D Tiles 1.1 Mesh 的流式层次、覆盖回退、资源预算与生命周期验证。',
    badge: 'Tiles / Mesh',
    routePath: '/standalone/tiles-mesh',
    previewClassName: 'preview-mesh-helmet',
    keywords: ['3d tiles', 'tiles', 'mesh', 'streaming', 'lod', '预算'],
    graphicsControlStrategy: 'switchable',
    presetKey: 'none',
  },
  {
    slug: 'mesh-damaged-helmet',
    title: 'Damaged Helmet Mesh',
    eyebrow: 'glTF Static Mesh',
    description: '加载带外部 BIN 与 PBR 纹理的 Damaged Helmet，验证 glTF Mesh 链路。',
    badge: 'Mesh / glTF',
    routePath: '/standalone/mesh-damaged-helmet',
    previewClassName: 'preview-mesh-helmet',
    keywords: ['mesh', 'gltf', 'helmet', 'pbr', 'texture'],
    graphicsControlStrategy: 'switchable',
    presetKey: 'coreMesh',
  },
  {
    slug: 'mesh-horse',
    title: 'Horse Mesh',
    eyebrow: 'GLB Static Mesh',
    description: '加载单文件 Horse.glb，验证 GLB 静态 Mesh 与内嵌资源链路。',
    badge: 'Mesh / GLB',
    routePath: '/standalone/mesh-horse',
    previewClassName: 'preview-mesh-horse',
    keywords: ['mesh', 'glb', 'horse', 'static mesh'],
    graphicsControlStrategy: 'switchable',
    presetKey: 'coreMesh',
  },
  {
    slug: 'mesh-ktx2',
    title: 'KTX2 / PNG 混合纹理',
    eyebrow: 'BasisU LDR',
    description: '2048 KTX2 与 PNG 共用纹理尺寸上限，验证 mip 选择、图片缩放及透明窗口。',
    badge: 'Mesh / KTX2',
    routePath: '/standalone/mesh-ktx2',
    previewClassName: 'preview-mesh-helmet',
    keywords: ['mesh', 'ktx2', 'basisu', 'texture', '透明'],
    graphicsControlStrategy: 'switchable',
    presetKey: 'coreMesh',
  },
  {
    slug: 'three-lines',
    title: 'Three Lines Sandbox',
    eyebrow: 'Three.js Playground',
    description: '线框与 sprite 实验页，用于验证基础 three.js 场景。',
    badge: '实验场',
    routePath: '/standalone/three-lines',
    previewClassName: 'preview-three-lines',
    previewImage: '/examples/previews/three-lines.jpg',
    keywords: ['three', 'lines', 'sandbox', 'sprite'],
    graphicsControlStrategy: 'locked',
    presetKey: 'none',
    graphicsControlReason: 'three.js Line2/LineMaterial 为 WebGL 专用 ShaderMaterial 实现，本页固定 WebGL2',
  },
  {
    slug: 'cad',
    title: 'CAD 绘图',
    eyebrow: 'Point Cloud CAD',
    description: 'CAD 平面图绘制、编辑与 DXF 导入导出，支持 WebGL2/WebGPU 切换。',
    badge: 'CAD 绘图',
    routePath: '/standalone/cad',
    previewClassName: 'preview-cad',
    previewImage: '/examples/previews/cad.jpg',
    keywords: ['cad', 'dxf', 'draw', 'survey', 'orthographic'],
    graphicsControlStrategy: 'switchable',
    presetKey: 'cadFixture',
  },
  {
    slug: 'cad-multi-viewport',
    title: 'CAD Dual Viewport',
    eyebrow: 'CAD Multi-View',
    description: '共享同一份 CAD 场景，在左右两个视口中切换 active view，联调多视口下的绘图、编辑与辅助工具。',
    badge: '双 CAD 视口',
    routePath: '/standalone/cad-multi-viewport',
    previewClassName: 'preview-cad-multi-viewport',
    keywords: ['cad', 'multi viewport', 'dual view', 'draw', 'debug'],
    graphicsControlStrategy: 'switchable',
    presetKey: 'cadFixture',
  },
  {
    slug: 'multi-viewport',
    title: 'Multi Viewport',
    eyebrow: 'View Registry Demo',
    description: '单个 engine 内同时创建主视图、inspect 视图和俯视图，演示独立相机、控制器与 layer mask。',
    badge: '多视口',
    routePath: '/standalone/multi-viewport',
    previewClassName: 'preview-multi-viewport',
    keywords: ['multi viewport', 'view registry', 'layer mask', 'inspect', 'overview'],
    graphicsControlStrategy: 'switchable',
    presetKey: 'pointCloudOnly',
    extraPluginLabels: [
      'ClipPlugin',
      'ClipOverlayPlugin',
      'GaussianSplatPlugin',
      'TilesPlugin',
    ],
  },
]

/**
 * 未进入产品示例目录的内部诊断页。这里是 catalog 的内部附录，避免另建产品 route registry。
 */
export const INTERNAL_EXAMPLE_ROUTES: InternalExampleRouteEntry[] = [
  {
    routePath: '/standalone/tiles-webgpu-validation',
    title: 'Tiles WebGPU Validation',
    helper: 'TilesMeshExampleView webgpuValidation',
    preset: '自定义 Core + Tiles 插件集',
    assetKind: '3D Tiles GLB / KTX2 / Meshopt',
    graphicsControlStrategy: 'locked',
    graphicsControlReason: '内部 Tiles WebGPU 定向验证页，固定 WebGPU 与自动化探针',
  },
  {
    routePath: '/standalone/low-memory-gltf-benchmark',
    title: 'Low Memory glTF Benchmark',
    helper: '页面内直接创建 PotreeEngine',
    preset: '自定义 Core + Mesh 插件集',
    assetKind: 'Mesh / glTF',
    graphicsControlStrategy: 'locked',
    graphicsControlReason: '基准直接用 WebGLRenderer.info 与 WEBGL_debug_renderer_info 计量，固定 WebGL2',
  },
  {
    routePath: '/standalone/webgpu-initialization-spike',
    title: 'WebGPU Initialization Spike',
    helper: '页面内 WebGPUBackend 初始化探针',
    preset: '无产品 preset',
    assetKind: '无资产 / 初始化诊断',
    graphicsControlStrategy: 'locked',
    graphicsControlReason: '内部 WebGPU 初始化验证页必须锁定 WebGPU 条件',
  },
  {
    routePath: '/standalone/webgpu-gsplat-cpu-baseline',
    title: 'WebGPU GSplat CPU Sort Baseline',
    helper: 'gsplatBackendControl',
    preset: '自定义 Core + GSplat 插件集',
    assetKind: '3DGS',
    graphicsControlStrategy: 'locked',
    graphicsControlReason: '内部 CPU 排序基准页用于固定条件验证，不作为产品切换页',
  },
  {
    routePath: '/standalone/webgpu-gsplat-gpu-baseline',
    title: 'WebGPU GSplat GPU Sort Baseline',
    helper: 'gsplatBackendControl',
    preset: '自定义 Core + GSplat 插件集',
    assetKind: '3DGS',
    graphicsControlStrategy: 'locked',
    graphicsControlReason: '内部 GPU 排序基准页用于固定条件验证，不作为产品切换页',
  },
  {
    routePath: '/standalone/webgpu-gsplat-radix-validation',
    title: 'WebGPU GSplat Radix Validation',
    helper: '页面内 WebGPU radix 验证配置',
    preset: '无产品 preset',
    assetKind: '3DGS 排序 fixture',
    graphicsControlStrategy: 'locked',
    graphicsControlReason: '内部 radix 算法验证页必须锁定 WebGPU 条件',
  },
]

export function getExampleBySlug(slug: string | undefined | null): ExampleCatalogEntry | null {
  if (!slug) {
    return null
  }

  return EXAMPLE_CATALOG.find((entry) => entry.slug === slug) ?? null
}
